import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Send, Search, X, Plus, FileText, Trash2, ChevronDown, Mail, MailCheck, Clock, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());

function ChipsInput({ value, onChange, placeholder, suggestions = [], testid }) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = draft.toLowerCase().trim();
    if (!q) return suggestions.slice(0, 8);
    return suggestions
      .filter(
        (s) =>
          s.email.toLowerCase().includes(q) ||
          (s.name || "").toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [draft, suggestions]);

  const add = (email) => {
    const e = String(email || "").trim().toLowerCase();
    if (!isEmail(e)) return;
    if (value.includes(e)) return;
    onChange([...value, e]);
    setDraft("");
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="relative" data-testid={testid}>
      <div className="min-h-[44px] border border-gray-200 rounded-sm px-2 py-1.5 flex flex-wrap gap-1.5 items-center bg-white focus-within:ring-2 focus-within:ring-[#002FA7]">
        {value.map((e, i) => (
          <span
            key={e + i}
            className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#002FA7]/8 text-[#002FA7] text-xs rounded-sm font-mono"
          >
            {e}
            <button
              type="button"
              onClick={() => remove(i)}
              className="opacity-60 hover:opacity-100"
              aria-label="remove"
              data-testid={`${testid}-remove-${i}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
            } else if (e.key === "Backspace" && !draft && value.length) {
              remove(value.length - 1);
            }
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={value.length ? "" : placeholder}
          className="flex-1 min-w-[160px] outline-none text-sm bg-transparent py-1 px-1"
          data-testid={`${testid}-input`}
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-sm shadow-md max-h-56 overflow-auto">
          {filtered.map((s) => (
            <button
              key={s.id || s.email}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(s.email)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between text-sm"
              data-testid={`${testid}-suggest-${s.email}`}
            >
              <span>
                <span className="font-medium text-gray-900">{s.name || s.email}</span>
                {s.name && <span className="text-gray-500 font-mono ml-2 text-xs">{s.email}</span>}
              </span>
              <Plus size={12} className="text-gray-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FilePickerDialog({ open, onOpenChange, files, selectedIds, onConfirm }) {
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [picked, setPicked] = useState(new Set(selectedIds));

  useEffect(() => {
    setPicked(new Set(selectedIds));
  }, [selectedIds, open]);

  const filtered = useMemo(() => {
    return files.filter((f) => {
      if (q && !f.original_name.toLowerCase().includes(q.toLowerCase())) return false;
      if (start && f.source_date < start) return false;
      if (end && f.source_date > end) return false;
      return true;
    });
  }, [files, q, start, end]);

  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((f) => picked.has(f.id));
  const toggleAll = () => {
    const next = new Set(picked);
    if (allFilteredSelected) filtered.forEach((f) => next.delete(f.id));
    else filtered.forEach((f) => next.add(f.id));
    setPicked(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm border-gray-200 max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display">Select files</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">Filter by date or search by name. Files already emailed are flagged.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="filename"
              className="pl-9 h-10 rounded-sm"
              data-testid="picker-search"
            />
          </div>
          <Input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="h-10 rounded-sm"
            data-testid="picker-start"
          />
          <Input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="h-10 rounded-sm"
            data-testid="picker-end"
          />
        </div>
        <div className="border border-gray-200 rounded-sm max-h-[420px] overflow-auto" data-testid="picker-list">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                <th className="text-left px-3 py-2 w-10">
                  <Checkbox
                    checked={allFilteredSelected}
                    onCheckedChange={toggleAll}
                    data-testid="picker-toggle-all"
                  />
                </th>
                <th className="text-left px-3 py-2">File</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-right px-3 py-2">Rows</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No files match</td></tr>
              )}
              {filtered.map((f) => (
                <tr
                  key={f.id}
                  className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${picked.has(f.id) ? "bg-[#002FA7]/5" : ""}`}
                  onClick={() => toggle(f.id)}
                  data-testid={`picker-row-${f.id}`}
                >
                  <td className="px-3 py-2"><Checkbox checked={picked.has(f.id)} onCheckedChange={() => toggle(f.id)} /></td>
                  <td className="px-3 py-2 truncate max-w-[360px]" title={f.original_name}>
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-gray-400" />
                      <span className="truncate">{f.original_name}</span>
                      {f.last_sent_at && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-wider uppercase font-semibold text-[#00C04B] border border-[#00C04B]/30 bg-[#00C04B]/5 rounded-sm whitespace-nowrap"
                          title={`Sent on ${f.last_sent_at.slice(0,10)} to ${f.last_sent_to || "—"}`}
                        >
                          <MailCheck size={9} /> {f.last_sent_at.slice(0,10)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{f.source_date}</td>
                  <td className="px-3 py-2 text-right font-mono">{f.total_rows}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <div className="flex-1 text-sm text-gray-600" data-testid="picker-count">
            {picked.size} selected
          </div>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white"
            onClick={() => { onConfirm(Array.from(picked)); onOpenChange(false); }}
            data-testid="picker-confirm"
          >
            Use selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SendEmail() {
  const [files, setFiles] = useState([]);
  const [pickedIds, setPickedIds] = useState([]); // string[]
  const [renameMap, setRenameMap] = useState({}); // file_id -> custom name
  const [pickerOpen, setPickerOpen] = useState(false);

  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [saveContacts, setSaveContacts] = useState(true);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  // Manage contacts
  const [contactsOpen, setContactsOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");

  // Scheduling
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [schedules, setSchedules] = useState([]);

  const reload = async () => {
    try {
      const [f, r, s] = await Promise.all([
        api.get("/files"),
        api.get("/recipients"),
        api.get("/email/scheduled"),
      ]);
      setFiles(f.data.files || []);
      setRecipients(r.data.recipients || []);
      setSchedules(s.data.schedules || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { reload(); }, []);

  // Default subject when picked changes
  useEffect(() => {
    if (!pickedIds.length || subject.trim()) return;
    const today = new Date().toISOString().slice(0, 10);
    setSubject(`Lead files — ${today}`);
  }, [pickedIds]); // eslint-disable-line

  // Default body
  useEffect(() => {
    if (!pickedIds.length || body.trim()) return;
    const list = pickedIds
      .map((id) => files.find((f) => f.id === id))
      .filter(Boolean)
      .map((f) => `• ${f.original_name} (${f.source_date}, ${f.total_rows} leads)`)
      .join("\n");
    setBody(`Hi,\n\nAttached are the following lead files:\n\n${list}\n\nThanks,\nOmnilink Solutions`);
  }, [pickedIds, files]); // eslint-disable-line

  const pickedFiles = pickedIds
    .map((id) => files.find((f) => f.id === id))
    .filter(Boolean);

  const removePicked = (id) => setPickedIds(pickedIds.filter((x) => x !== id));

  const setRenameFor = (id, name) => setRenameMap({ ...renameMap, [id]: name });

  const addContact = async () => {
    if (!isEmail(newEmail)) return toast.error("Enter a valid email");
    try {
      await api.post("/recipients", { email: newEmail, name: newName });
      setNewName(""); setNewEmail("");
      reload();
      toast.success("Contact saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const deleteContact = async (id) => {
    if (!window.confirm("Remove this contact?")) return;
    try {
      await api.delete(`/recipients/${id}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const onSend = async () => {
    if (pickedFiles.length === 0) return toast.error("Pick at least one file");
    if (to.length === 0) return toast.error("Add at least one recipient");
    if (!subject.trim()) return toast.error("Subject cannot be empty");
    if (scheduleMode && !scheduledAt) return toast.error("Pick a date & time to schedule");
    setLoading(true);
    try {
      const basePayload = {
        to,
        cc,
        subject: subject.trim(),
        body,
        save_recipients: saveContacts,
        attachments: pickedFiles.map((f) => ({
          file_id: f.id,
          custom_name: (renameMap[f.id] || f.original_name.replace(/\.[^.]+$/, "")).trim(),
        })),
      };
      if (scheduleMode) {
        // datetime-local value is in local timezone. Convert to UTC ISO.
        const localDate = new Date(scheduledAt);
        if (isNaN(localDate.getTime())) throw new Error("Invalid date/time");
        const iso = localDate.toISOString();
        await api.post("/email/schedule", { ...basePayload, scheduled_at: iso });
        toast.success(`Email scheduled for ${localDate.toLocaleString()}`);
      } else {
        const { data } = await api.post("/email/send", basePayload);
        toast.success(`Email sent — ${data.files_attached} file(s), ${data.total_rows} rows.`);
      }
      // Reset
      setPickedIds([]);
      setRenameMap({});
      setSubject("");
      setBody("");
      setScheduledAt("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelSchedule = async (sid) => {
    if (!window.confirm("Cancel this scheduled email?")) return;
    try {
      await api.delete(`/email/scheduled/${sid}`);
      toast.success("Scheduled email cancelled");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="send-email-page">
      <div className="mb-8 fade-up flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gray-500">Distribute</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-gray-950 mt-2">Send Files via Email</h1>
          <p className="text-gray-600 mt-2">
            Attach one or many files, rename each before send, choose recipients — done.
          </p>
        </div>
        <Popover open={contactsOpen} onOpenChange={setContactsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="rounded-sm" data-testid="manage-contacts-btn">
              <Mail size={14} className="mr-1.5" /> Contacts <ChevronDown size={14} className="ml-1" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[360px] rounded-sm p-4">
            <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-3">Saved Contacts</div>
            <div className="space-y-2 max-h-56 overflow-auto mb-3">
              {recipients.length === 0 && <div className="text-sm text-gray-500">No saved contacts yet.</div>}
              {recipients.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 border border-gray-100 rounded-sm px-2 py-1.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.name || "(no name)"}</div>
                    <div className="text-xs font-mono text-gray-500 truncate">{r.email}</div>
                  </div>
                  <button onClick={() => deleteContact(r.id)} className="text-gray-400 hover:text-[#FF3B30]" data-testid={`contact-delete-${r.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-100 pt-3 space-y-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name (optional)"
                className="rounded-sm h-9"
                data-testid="contact-name-input"
              />
              <Input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="email@example.com"
                className="rounded-sm h-9 font-mono"
                data-testid="contact-email-input"
              />
              <Button onClick={addContact} className="w-full rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="contact-add-btn">
                <Plus size={14} className="mr-1" /> Add contact
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: file selection + per-attachment names */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500">Attachments</div>
                <div className="text-sm text-gray-600 mt-1">Pick by date or filename · rename each before sending</div>
              </div>
              <Button
                onClick={() => setPickerOpen(true)}
                className="rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white"
                data-testid="open-picker-btn"
              >
                <Plus size={14} className="mr-1" /> Choose files
              </Button>
            </div>
            {pickedFiles.length === 0 ? (
              <div className="border border-dashed border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500" data-testid="no-attachments">
                No files selected yet.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-sm overflow-hidden" data-testid="selected-files-table">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                      <th className="text-left px-3 py-2">Source File</th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-right px-3 py-2">Rows</th>
                      <th className="text-left px-3 py-2 w-[36%]">Attachment Name (.csv)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pickedFiles.map((f) => (
                      <tr key={f.id} className="border-t border-gray-100" data-testid={`attachment-row-${f.id}`}>
                        <td className="px-3 py-2 max-w-[260px] truncate" title={f.original_name}>{f.original_name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{f.source_date}</td>
                        <td className="px-3 py-2 text-right font-mono">{f.total_rows}</td>
                        <td className="px-3 py-2">
                          <Input
                            value={renameMap[f.id] ?? f.original_name.replace(/\.[^.]+$/, "")}
                            onChange={(e) => setRenameFor(f.id, e.target.value)}
                            className="rounded-sm h-9 font-mono text-xs"
                            data-testid={`rename-${f.id}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => removePicked(f.id)} className="text-gray-400 hover:text-[#FF3B30]" data-testid={`remove-attachment-${f.id}`}>
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white">
            <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">Message</div>
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-1 h-10 rounded-sm"
                  placeholder="Lead files — 2026-05-30"
                  data-testid="email-subject"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">Body</Label>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  className="mt-1 rounded-sm font-sans"
                  placeholder="Hi team..."
                  data-testid="email-body"
                />
                <div className="text-xs text-gray-500 mt-1">Newlines preserved. Plain text becomes HTML on send.</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right: recipients + send */}
        <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white h-fit sticky top-6">
          <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">Recipients</div>

          <div className="space-y-4">
            <div>
              <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">To</Label>
              <ChipsInput
                value={to}
                onChange={setTo}
                placeholder="type email + Enter"
                suggestions={recipients}
                testid="to-chips"
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">CC</Label>
              <ChipsInput
                value={cc}
                onChange={setCc}
                placeholder="optional"
                suggestions={recipients}
                testid="cc-chips"
              />
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-700 pt-2">
              <Checkbox
                checked={saveContacts}
                onCheckedChange={setSaveContacts}
                className="mt-0.5"
                data-testid="save-contacts-checkbox"
              />
              <span>
                <span className="font-medium text-gray-900">Save recipients</span>
                <span className="block text-xs text-gray-500 mt-0.5">For next time's autocomplete</span>
              </span>
            </label>

            <div className="border-t border-gray-100 pt-4 space-y-3">
              <div className="grid grid-cols-2 gap-2" data-testid="when-mode-tabs">
                <button
                  type="button"
                  onClick={() => setScheduleMode(false)}
                  className={`text-xs font-medium px-3 py-2 rounded-sm border transition-all ${!scheduleMode ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                  data-testid="mode-send-now"
                >
                  <Send size={12} className="inline mr-1.5" /> Send now
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleMode(true)}
                  className={`text-xs font-medium px-3 py-2 rounded-sm border transition-all ${scheduleMode ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                  data-testid="mode-schedule"
                >
                  <Clock size={12} className="inline mr-1.5" /> Schedule
                </button>
              </div>
              {scheduleMode && (
                <div>
                  <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">When (your local time)</Label>
                  <Input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="mt-1 h-10 rounded-sm font-mono"
                    data-testid="schedule-datetime"
                  />
                  <div className="text-xs text-gray-500 mt-1">Email will be sent automatically at the chosen time.</div>
                </div>
              )}
            </div>

            <div className="pt-2">
              <div className="text-xs text-gray-600 mb-3 leading-relaxed">
                {pickedFiles.length} file{pickedFiles.length === 1 ? "" : "s"} · {to.length + cc.length} recipient{to.length + cc.length === 1 ? "" : "s"} · {pickedFiles.reduce((s, f) => s + (f.total_rows || 0), 0).toLocaleString()} rows
              </div>
              <Button
                onClick={onSend}
                disabled={loading || pickedFiles.length === 0 || to.length === 0}
                className="w-full h-11 rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white"
                data-testid="send-email-btn"
              >
                {loading ? (scheduleMode ? "Scheduling…" : "Sending…") : (
                  scheduleMode ? (
                    <><Clock size={16} className="mr-2" /> Schedule Email</>
                  ) : (
                    <><Send size={16} className="mr-2" /> Send Email</>
                  )
                )}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <FilePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        files={files}
        selectedIds={pickedIds}
        onConfirm={(ids) => setPickedIds(ids)}
      />

      {schedules.length > 0 && (
        <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white mt-8" data-testid="scheduled-list">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500">Scheduled Emails</div>
              <div className="text-sm text-gray-600 mt-1">Pending sends will fire automatically.</div>
            </div>
            <CalendarClock className="text-[#002FA7]" size={18} />
          </div>
          <div className="border border-gray-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">To</th>
                  <th className="text-left px-3 py-2">Files</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((s) => {
                  const when = s.scheduled_at ? new Date(s.scheduled_at).toLocaleString() : "—";
                  return (
                    <tr key={s.id} className="border-t border-gray-100" data-testid={`schedule-row-${s.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{when}</td>
                      <td className="px-3 py-2 max-w-[260px] truncate" title={s.subject}>{s.subject}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-700 max-w-[200px] truncate">{(s.to || []).join(", ")}</td>
                      <td className="px-3 py-2 font-mono text-xs">{(s.attachments || []).length}</td>
                      <td className="px-3 py-2">
                        {s.status === "pending" && <span className="text-[10px] tracking-wider uppercase font-semibold text-[#007AFF] border border-[#007AFF]/30 bg-[#007AFF]/5 px-2 py-0.5 rounded-sm">Pending</span>}
                        {s.status === "processing" && <span className="text-[10px] tracking-wider uppercase font-semibold text-amber-700 border border-amber-300 bg-amber-50 px-2 py-0.5 rounded-sm">Sending…</span>}
                        {s.status === "sent" && <span className="text-[10px] tracking-wider uppercase font-semibold text-[#00C04B] border border-[#00C04B]/30 bg-[#00C04B]/5 px-2 py-0.5 rounded-sm">Sent</span>}
                        {s.status === "failed" && <span className="text-[10px] tracking-wider uppercase font-semibold text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-2 py-0.5 rounded-sm" title={s.error}>Failed</span>}
                        {s.status === "cancelled" && <span className="text-[10px] tracking-wider uppercase font-semibold text-gray-600 border border-gray-300 bg-gray-50 px-2 py-0.5 rounded-sm">Cancelled</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {s.status === "pending" && (
                          <button onClick={() => cancelSchedule(s.id)} className="text-gray-400 hover:text-[#FF3B30] transition-colors" data-testid={`cancel-schedule-${s.id}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
